import React from "react";
import { I18n } from "@aws-amplify/core";
import { connect } from "react-redux";
import { Box, withStyles } from "@material-ui/core";
import { get, filter, map, size } from "lodash";
import clsx from "clsx";
import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";

import CloseIcon from "../../Common/CloseIcon";
import UpdateOccurrence from "../AddOccurrence/UpdateOccurrence";
import SchedulerOccurrenceProfile from "../SchedulerOccurrenceProfile/SchedulerOccurrenceProfile";
import { formatDate } from "../../../utils/date";
import { getAttributeValue } from "../../../utils/profile";
import {
  getOccurrenceArrival,
  getOccurrenceEnd,
  getOccurrenceStart,
} from "../../../utils/scheduler";
import images from "../../../assets/img/images";
import colors from "../../../assets/style/colors";

const styles = {
  dialog: {
    minWidth: "370px",
    padding: "12px 24px 24px",
  },
  container: {
    height: "100%",
  },
  closeIcon: {
    display: "flex",
    justifyContent: "flex-end",
    marginRight: "-12px",
  },
  content: {},
  zoneBox: {
    padding: "0px 5px",
    minWidth: "40px",
    height: "22px",
    marginRight: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  zoneBoxNoColor: {
    border: `0.5px solid ${colors.black}`,
  },
  editIcon: {
    cursor: "pointer",
  },
};

const SchedulerOccurrenceView = (props) => {
  const {
    classes,
    language,
    open,
    setOpen,
    occurrence,
    timeSlots,
    refresh,
    customTimeSlot,
    edit,
    del,
  } = props;
  const [editMode, setEditMode] = React.useState(edit);

  const occurrenceArrival = getOccurrenceArrival(occurrence);
  const occurrenceStart = getOccurrenceStart(occurrence);
  const occurrenceEnd = getOccurrenceEnd(occurrence);

  const hostProfile = get(
    filter(
      get(occurrenceArrival, ["profiles"]),
      (p) => get(p, ["association_type", "id"]) === 1
    ),
    [0, "profile"]
  );

  const visitors = filter(
    get(occurrenceArrival, ["profiles"]),
    (p) => get(p, ["association_type", "id"]) === 2
  );

  function parentClose() {
    setOpen(false)
  }

  if (editMode) {
    return (
      <UpdateOccurrence
        open={editMode}
        setOpen={setEditMode}
        occurrence={occurrence}
        timeSlots={timeSlots}
        refresh={refresh}
        customTimeSlot={customTimeSlot}
        parentClose={parentClose}
        del={del}
      />
    );
  }

  const scheduleName =
    get(occurrence, ["schedule", "name", language]) ||
    get(occurrence, ["schedule", "name", language === "en" ? "fr" : "en"]);

  const scheduleType = get(occurrence, ["schedule", "type", "name", language]);

  return (
    <Dialog
      open={open}
      classes={{ paper: classes.dialog }}
      data-test="scheduler-occurrence-view-component"
    >
      <Grid
        container
        wrap="nowrap"
        direction="column"
        alignItems="stretch"
        className={classes.container}
      >
        <Grid item>
          <Grid container alignItems="center">
            <Grid item xs={2} style={{ marginLeft: "-12px" }} />
            <Grid item xs className="dialogTitle">
              {I18n.get("scheduler_schedulerOccurrence").toUpperCase()}
            </Grid>
            <Grid item xs={2} className={classes.closeIcon}>
              <CloseIcon closeFunction={() => setOpen(false)} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item style={{ marginTop: "16px" }}>
          <Grid container>
            <Grid item xs>
              <Box className="bold-500">{I18n.get("site_dateTime")}</Box>
              <Box className="text-54">
                {formatDate(get(occurrence, ["when"]), "dddd, MMM. Do, YYYY")}
              </Box>
              <Box className="text-54">
                {`${formatDate(occurrenceStart, "h:mm a")} - ${formatDate(
                  occurrenceEnd,
                  "h:mm a"
                )}`}
              </Box>
            </Grid>
            <Grid item>
              <img
                src={images.editIconColored}
                alt="edit"
                className={classes.editIcon}
                onClick={() => { setEditMode(true); }}
              />
            </Grid>
          </Grid>
        </Grid>
        {scheduleName && (
          <Grid item style={{ marginTop: "24px" }}>
            <Box className="bold-500">{I18n.get("profiles_name")}</Box>
            <Box className="text-54">{scheduleName}</Box>
          </Grid>
        )}
        {scheduleType && (
          <Grid item style={{ marginTop: "24px" }}>
            <Box className="bold-500">{I18n.get("scheduler_scheduleType")}</Box>
            <Box className="text-54">{scheduleType}</Box>
          </Grid>
        )}
        {getAttributeValue(occurrence, 19) && (
          <Grid item style={{ marginTop: "24px" }}>
            <Box className="bold-500">{I18n.get("site_comments")}</Box>
            <Box className="text-54">{getAttributeValue(occurrence, 19)}</Box>
          </Grid>
        )}
        <Grid item className={classes.content}>
          <Box className="bold-500" style={{ marginTop: "24px" }}>
            {I18n.get("profiles_location2")}
          </Box>
          <Grid container alignItems="center" wrap="nowrap">
            {map(get(occurrenceArrival, ["zones"]), (z) => (
              <Grid
                item
                key={z.id}
                className={clsx(classes.zoneBox, {
                  [classes.zoneBoxNoColor]: !get(z, ["zone", "color", "code"]),
                })}
                style={{
                  backgroundColor: `#${get(z, ["zone", "color", "code"])}`,
                }}
              >
                {get(z, ["zone", "name"])}
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item className={classes.content}>
          <Box className="bold-500" style={{ marginTop: "24px" }}>
            {I18n.get("scheduler_host")}
          </Box>
          <SchedulerOccurrenceProfile profileId={get(hostProfile, ["id"])} />
          <Box className="bold-500" style={{ marginTop: "24px" }}>
            {`${size(visitors)} ${I18n.get("scheduler_visitors")}`}
          </Box>
          {map(visitors, (v, index) => (
            <SchedulerOccurrenceProfile
              key={index}
              profileId={get(v, ["profile", "id"])}
            />
          ))}
        </Grid>
      </Grid>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(SchedulerOccurrenceView)
);
